import * as THREE from 'three';
import { VRM, VRMExpressionManager, VRMExpressionPresetName, VRMLoaderPlugin, VRMUtils, VRMLookAt } from '@pixiv/three-vrm';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { loadVRMAnimation } from './loadVRMAnimation';
import { createVRMAnimationClip, VRMLookAtQuaternionProxy, type VRMAnimation } from '@pixiv/three-vrm-animation';

export interface VrmManagerConfig {
  modelPath?: string;
  animationPath?: string;
  deviceTier?: 'low' | 'medium' | 'high';
  enableControls?: boolean;
  cameraPosition?: { x: number; y: number; z: number };
  lookAtPosition?: { x: number; y: number; z: number };
}

export class VrmManager {
  private static instance: VrmManager | null = null;
  
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private vrm: VRM | null = null;
  private mixer: THREE.AnimationMixer | null = null;
  private controls: OrbitControls | null = null;
  private clock = new THREE.Clock();
  private container: HTMLElement | null = null;
  private animationFrameId: number | null = null;
  private loader: GLTFLoader;
  private currentAnimation: VRMAnimation | null = null;
  
  // Configuration
  private config: Required<VrmManagerConfig> = {
    modelPath: '/models/character-01.vrm',
    animationPath: '/animations/VRMA_MotionPack/vrma/VRMA_02.vrma',
    deviceTier: 'high',
    enableControls: false, // デスクトップでもマウス操作を無効化
    cameraPosition: { x: 0, y: 1.4, z: 2.5 }, // カメラを少し近づけてモバイルでVRMが大きく見えるように
    lookAtPosition: { x: 0, y: 1.0, z: 0 }
  };
  
  // Blink control
  private blinkTimer: NodeJS.Timeout | null = null;
  private isBlinking = false;
  
  // Scale constants
  private readonly BASE_MODEL_SCALE = 1.0;
  private readonly MOBILE_SCALE_FACTOR = 0.8; // モバイルのスケールを0.6から0.8に変更（大きくする）

  private constructor() {
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    
    // Mobile-optimized renderer settings
    const isMobile = window.innerWidth <= 768;
    this.renderer = new THREE.WebGLRenderer({ 
      antialias: !isMobile, // Disable antialiasing on mobile for performance
      alpha: true,
      powerPreference: isMobile ? 'default' : 'high-performance',
      premultipliedAlpha: false, // 透明度計算の改善
      preserveDrawingBuffer: false // パフォーマンス向上
    });
    
    this.loader = new GLTFLoader();
    this.loader.register((parser) => new VRMLoaderPlugin(parser));
    
    this.setupScene();
  }

  public static getInstance(): VrmManager {
    if (!VrmManager.instance) {
      VrmManager.instance = new VrmManager();
    }
    return VrmManager.instance;
  }

  private setupScene(): void {
    // Beach-themed lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(1, 1, 1);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    this.scene.add(directionalLight);
    
    // Soft rim light for beach atmosphere
    const rimLight = new THREE.DirectionalLight(0x87ceeb, 0.3);
    rimLight.position.set(-1, 0.5, -1);
    this.scene.add(rimLight);
    
    // Camera setup
    this.camera.position.set(
      this.config.cameraPosition.x,
      this.config.cameraPosition.y,
      this.config.cameraPosition.z
    );
    this.camera.lookAt(
      this.config.lookAtPosition.x,
      this.config.lookAtPosition.y,
      this.config.lookAtPosition.z
    );
    
    // Renderer setup with mobile optimizations
    const isMobile = window.innerWidth <= 768;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.5 : 2));
    
    if (isMobile) {
      // Mobile: Reduced quality for better performance
      this.renderer.shadowMap.enabled = false; // Disable shadows on mobile
    } else {
      // Desktop: High quality settings
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    }
    
    // 透明背景を維持してモバイル白背景問題を防止
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    
    // モバイルでの追加の透明度対策
    if (isMobile && this.renderer.domElement) {
      this.renderer.domElement.style.backgroundColor = 'transparent';
      this.renderer.domElement.style.opacity = '1';
      
      // iOS Safari向けの追加対策
      this.renderer.domElement.style.webkitBackfaceVisibility = 'hidden';
      this.renderer.domElement.style.backfaceVisibility = 'hidden';
      this.renderer.domElement.style.transform = 'translateZ(0)';
      this.renderer.domElement.style.isolation = 'isolate';
      
      // 親要素にも透明背景を強制
      setTimeout(() => {
        if (this.renderer.domElement.parentElement) {
          this.renderer.domElement.parentElement.style.backgroundColor = 'transparent';
        }
      }, 100);
    }
  }

  public async initialize(container: HTMLElement, config?: Partial<VrmManagerConfig>): Promise<void> {
    this.container = container;
    this.config = { ...this.config, ...config };
    
    // Set renderer size
    const { width, height } = container.getBoundingClientRect();
    this.renderer.setSize(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    
    // Add renderer to container
    container.appendChild(this.renderer.domElement);
    
    // Setup controls if enabled
    if (this.config.enableControls) {
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.target.set(
        this.config.lookAtPosition.x,
        this.config.lookAtPosition.y,
        this.config.lookAtPosition.z
      );
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.05;
      this.controls.screenSpacePanning = false;
      this.controls.minDistance = 1;
      this.controls.maxDistance = 10;
      this.controls.maxPolarAngle = Math.PI / 2;
      this.controls.update();
      // autoRotate 기능は削除
    }
    
    // Handle resize
    window.addEventListener('resize', this.handleResize);
    
    // Load VRM model
    await this.loadModel(this.config.modelPath);
    
    // Load initial animation
    if (this.config.animationPath) {
      await this.loadAnimation(this.config.animationPath);
    }
    
    // Start render loop
    this.animate();
  }

  private handleResize = (): void => {
    if (!this.container) {
      return;
    }
    
    const { width, height } = this.container.getBoundingClientRect();
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  };

  public async loadModel(modelPath: string): Promise<void> {
    try {
      // Clean up existing model
      if (this.vrm) {
        this.scene.remove(this.vrm.scene);
        VRMUtils.deepDispose(this.vrm.scene);
        this.vrm = null;
        this.mixer = null;
      }
      
      // Load new model
      const gltf = await this.loader.loadAsync(modelPath);
      const vrm = gltf.userData.vrm as VRM;
      
      if (!vrm) {
        throw new Error('Failed to load VRM');
      }
      
      this.vrm = vrm;
      
      // Create VRMLookAtQuaternionProxy to suppress warning
      interface VRMLookAtWithProxy extends VRMLookAt {
        quaternionProxy?: VRMLookAtQuaternionProxy;
      }
      const lookAtWithProxy = vrm.lookAt as VRMLookAtWithProxy | undefined;
      if (lookAtWithProxy && !lookAtWithProxy.quaternionProxy) {
        lookAtWithProxy.quaternionProxy = new VRMLookAtQuaternionProxy(lookAtWithProxy);
      }
      
      // Apply scale based on device
      const isMobile = window.innerWidth <= 768;
      const scale = this.BASE_MODEL_SCALE * (isMobile ? this.MOBILE_SCALE_FACTOR : 1);
      vrm.scene.scale.set(scale, scale, scale);
      
      // Center the model
      const boundingBox = new THREE.Box3().setFromObject(vrm.scene);
      const center = boundingBox.getCenter(new THREE.Vector3());
      vrm.scene.position.x = -center.x;
      vrm.scene.position.z = -center.z;
      // VRMを少し高く配置（デスクトップとモバイル両方）
      const yOffset = isMobile ? 0.2 : 0.1;
      vrm.scene.position.y = -boundingBox.min.y + yOffset;
      
      // Add to scene
      this.scene.add(vrm.scene);
      
      // Create animation mixer
      this.mixer = new THREE.AnimationMixer(vrm.scene);
      
      // --- SpringBone 調整 ---
      if (vrm.springBoneManager) {
        try {
          interface SpringBoneManagerExtended {
            gravityDir?: THREE.Vector3;
            joints?: Set<unknown>;
            colliders?: unknown[];
          }
          const sbManager = vrm.springBoneManager as unknown as SpringBoneManagerExtended;
          
          // デフォルトの重力設定
          const gravityDir = new THREE.Vector3(0, -1, 0);
          sbManager.gravityDir?.copy?.(gravityDir);
          
          // SpringBoneはデフォルト値を使用（カスタマイズなし）
          
          // デバッグログ（開発環境のみ）
          if (process.env.NODE_ENV === 'development') {
            console.warn('SpringBone default settings applied:', {
              gravityDir,
              joints: sbManager.joints?.size || 0,
              colliders: sbManager.colliders?.length || 0
            });
          }
        } catch (e) {
          console.warn('SpringBone setup failed:', e);
        }
      }
      
      // Start blinking
      this.startBlinking();
      
    } catch (error) {
      console.error('Failed to load VRM model:', error);
    }
  }

  public async loadAnimation(animationPath: string): Promise<void> {
    if (!this.vrm || !this.mixer) {
      return;
    }
    
    try {
      // Stop current animation
      this.mixer.stopAllAction();
      
      // Ensure VRMLookAtQuaternionProxy exists before loading animation
      interface VRMLookAtWithProxy extends VRMLookAt {
        quaternionProxy?: VRMLookAtQuaternionProxy;
      }
      const lookAtWithProxy = this.vrm.lookAt as VRMLookAtWithProxy | undefined;
      if (lookAtWithProxy && !lookAtWithProxy.quaternionProxy) {
        lookAtWithProxy.quaternionProxy = new VRMLookAtQuaternionProxy(lookAtWithProxy);
      }
      
      // Load new animation
      this.currentAnimation = await loadVRMAnimation(animationPath, this.vrm);
      
      if (this.currentAnimation) {
        // Create animation clip from the loaded VRM animation
        const animationClip = createVRMAnimationClip(this.currentAnimation, this.vrm);
        
        if (animationClip) {
          // Create and play the animation action
          const animationAction = this.mixer.clipAction(animationClip);
          animationAction.setLoop(THREE.LoopRepeat, Infinity);
          animationAction.play();
          
          // Only log in development mode
          if (process.env.NODE_ENV === 'development') {
            console.warn(`Successfully playing VRM animation: ${animationPath}`);
          }
        } else {
          console.error(`Failed to create animation clip for: ${animationPath}`);
        }
      }
    } catch (error) {
      console.error('Failed to load animation:', error);
    }
  }

  private startBlinking(): void {
    if (this.blinkTimer) {
      clearTimeout(this.blinkTimer);
    }
    
    const scheduleBlink = () => {
      // Random interval between 2-6 seconds
      const interval = 2000 + Math.random() * 4000;
      
      this.blinkTimer = setTimeout(() => {
        if (this.vrm && !this.isBlinking) {
          this.performBlink();
        }
        scheduleBlink();
      }, interval);
    };
    
    scheduleBlink();
  }

  private performBlink(): void {
    if (!this.vrm?.expressionManager) {
      return;
    }
    
    this.isBlinking = true;
    const expressionManager = this.vrm.expressionManager as VRMExpressionManager;
    
    // Close eyes
    expressionManager.setValue(VRMExpressionPresetName.Blink, 1);
    expressionManager.update();
    
    // Open eyes after 100-200ms
    const blinkDuration = 100 + Math.random() * 100;
    setTimeout(() => {
      if (this.vrm?.expressionManager) {
        expressionManager.setValue(VRMExpressionPresetName.Blink, 0);
        expressionManager.update();
        this.isBlinking = false;
      }
    }, blinkDuration);
  }

  public setExpression(expressionName: VRMExpressionPresetName, value: number): void {
    if (!this.vrm?.expressionManager) {
      return;
    }
    
    const expressionManager = this.vrm.expressionManager as VRMExpressionManager;
    expressionManager.setValue(expressionName, value);
    expressionManager.update();
  }

  private animate = (): void => {
    this.animationFrameId = requestAnimationFrame(this.animate);
    
    const deltaTime = this.clock.getDelta();
    
    // Update controls
    if (this.controls?.enabled) {
      this.controls.update();
    }
    
    // Update animation
    if (this.mixer) {
      this.mixer.update(deltaTime);
    }
    
    // Update VRM
    if (this.vrm) {
      this.vrm.update(deltaTime);
    }
    
    // Render
    this.renderer.render(this.scene, this.camera);
  };

  public dispose(): void {
    // Stop animation loop
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    
    // Stop blinking
    if (this.blinkTimer) {
      clearTimeout(this.blinkTimer);
      this.blinkTimer = null;
    }
    
    // Clean up VRM
    if (this.vrm) {
      this.scene.remove(this.vrm.scene);
      VRMUtils.deepDispose(this.vrm.scene);
      this.vrm = null;
    }
    
    // Clean up controls
    if (this.controls) {
      this.controls.dispose();
      this.controls = null;
    }
    
    // Clean up renderer
    if (this.container && this.renderer.domElement.parentElement === this.container) {
      this.container.removeChild(this.renderer.domElement);
    }
    this.renderer.dispose();
    
    // Remove event listeners
    window.removeEventListener('resize', this.handleResize);
    
    // Clear instance
    VrmManager.instance = null;
  }

  // Getters for external control
  public get currentVRM(): VRM | null {
    return this.vrm;
  }

  public get isInitialized(): boolean {
    return this.vrm !== null;
  }

  // Camera control methods
  public panCamera(deltaX: number, deltaY: number): void {
    if (!this.camera) {
      return;
    }
    
    // Calculate pan speed based on camera distance
    const distance = this.camera.position.length();
    const panSpeed = distance * 0.001;
    
    // Get camera's right and up vectors
    const right = new THREE.Vector3();
    const up = new THREE.Vector3();
    this.camera.getWorldDirection(new THREE.Vector3());
    right.setFromMatrixColumn(this.camera.matrix, 0);
    up.setFromMatrixColumn(this.camera.matrix, 1);
    
    // Apply pan
    this.camera.position.addScaledVector(right, -deltaX * panSpeed);
    this.camera.position.addScaledVector(up, deltaY * panSpeed);
    
    // Update controls target if exists
    if (this.controls) {
      this.controls.target.addScaledVector(right, -deltaX * panSpeed);
      this.controls.target.addScaledVector(up, deltaY * panSpeed);
    } else {
      // If no controls, update the lookAt position
      const target = new THREE.Vector3(
        this.config.lookAtPosition.x,
        this.config.lookAtPosition.y,
        this.config.lookAtPosition.z
      );
      target.addScaledVector(right, -deltaX * panSpeed);
      target.addScaledVector(up, deltaY * panSpeed);
      this.camera.lookAt(target);
    }
  }
  
  public rotateCamera(deltaX: number, deltaY: number): void {
    if (!this.camera) {
      return;
    }
    
    // Get the target position (lookAt position or controls target)
    const target = this.controls 
      ? this.controls.target 
      : new THREE.Vector3(
          this.config.lookAtPosition.x,
          this.config.lookAtPosition.y,
          this.config.lookAtPosition.z
        );
    
    // Get current position relative to target
    const offset = new THREE.Vector3();
    const spherical = new THREE.Spherical();
    
    offset.copy(this.camera.position).sub(target);
    spherical.setFromVector3(offset);
    
    // Apply rotation
    spherical.theta -= deltaX * 0.01; // Horizontal rotation
    spherical.phi += deltaY * 0.01;   // Vertical rotation
    
    // Clamp vertical rotation
    spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, spherical.phi));
    
    // Update camera position
    offset.setFromSpherical(spherical);
    this.camera.position.copy(target).add(offset);
    
    // Update controls if they exist
    if (this.controls) {
      this.controls.update();
    }
    
    // Update camera lookAt
    this.camera.lookAt(target);
  }
  
  public zoomCamera(delta: number): void {
    if (!this.camera) {
      return;
    }
    
    const zoomSpeed = 0.1;
    const minDistance = 1;
    const maxDistance = 10;
    
    // Get the target position (lookAt position or controls target)
    const target = this.controls 
      ? this.controls.target 
      : new THREE.Vector3(
          this.config.lookAtPosition.x,
          this.config.lookAtPosition.y,
          this.config.lookAtPosition.z
        );
    
    // Calculate new distance
    const offset = new THREE.Vector3();
    offset.copy(this.camera.position).sub(target);
    const currentDistance = offset.length();
    const newDistance = Math.max(minDistance, Math.min(maxDistance, currentDistance * (1 + delta * zoomSpeed)));
    
    // Apply new distance
    offset.normalize().multiplyScalar(newDistance);
    this.camera.position.copy(target).add(offset);
    
    // Update controls if they exist
    if (this.controls) {
      this.controls.update();
    }
    
    // Update camera lookAt
    this.camera.lookAt(target);
  }
  
  public resetCamera(): void {
    // Reset to default position
    this.camera.position.set(
      this.config.cameraPosition.x,
      this.config.cameraPosition.y,
      this.config.cameraPosition.z
    );
    
    if (this.controls) {
      this.controls.target.set(
        this.config.lookAtPosition.x,
        this.config.lookAtPosition.y,
        this.config.lookAtPosition.z
      );
      this.controls.update();
    }
    
    this.camera.lookAt(
      this.config.lookAtPosition.x,
      this.config.lookAtPosition.y,
      this.config.lookAtPosition.z
    );
  }

  public async updateConfig(config: Partial<VrmManagerConfig>): Promise<void> {
    const previousConfig = { ...this.config };
    this.config = { ...this.config, ...config };
    
    // Only reload model if model path changed
    if (config.modelPath && config.modelPath !== previousConfig.modelPath) {
      await this.loadModel(config.modelPath);
    }
    
    // Only reload animation if animation path changed and we have a VRM
    if (config.animationPath && config.animationPath !== previousConfig.animationPath && this.vrm) {
      await this.loadAnimation(config.animationPath);
    }
    
    // Update controls if they exist
    if (this.controls && typeof config.enableControls === 'boolean') {
      this.controls.enabled = config.enableControls;
    }
  }
}